import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Home, Plane, FileCheck, ShieldCheck, ClipboardList, AlertCircle, Download, CheckCircle2, ArrowRight } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const permitTypes = [{
  title: 'Phytosanitary Certificate',
  description: 'Required for all plant and plant product exports. Certifies that consignments meet the phytosanitary requirements of the importing country and conform to the International Plant Protection Convention (IPPC) standards.',
  icon: ShieldCheck,
  color: 'bg-green-600',
  requirements: ['Completed application form', 'Copy of commercial invoice', 'Packing list', 'Inspection of consignment at point of exit'],
  fee: 'UGX 50,000 per certificate',
  processing: '2-3 working days'
}, {
  title: 'Import Permit for Plant Materials',
  description: 'Mandatory for importing seeds, seedlings, cuttings, and other plant propagation materials into Uganda. Ensures imported materials are free from pests and diseases.',
  icon: FileCheck,
  color: 'bg-blue-600',
  requirements: ['Application letter to Commissioner Crop Protection', 'Proforma invoice from supplier', 'Phytosanitary certificate from country of origin', 'Pest risk analysis (for new introductions)'],
  fee: 'UGX 100,000 per permit',
  processing: '5-7 working days'
}, {
  title: 'Veterinary Health Certificate',
  description: 'Required for export of live animals, animal products, and by-products. Certifies that animals and products are free from notifiable diseases and meet international standards.',
  icon: ShieldCheck,
  color: 'bg-purple-600',
  requirements: ['Application to Commissioner Animal Health', 'Proof of origin and ownership', 'Laboratory test results (disease-free status)', 'Inspection by authorized veterinary officer'],
  fee: 'UGX 80,000 per certificate',
  processing: '3-5 working days'
}, {
  title: 'Import Permit for Animal Products',
  description: 'Required for importing live animals, semen, embryos, hides, skins, and other animal products. Protects Uganda from introduction of transboundary animal diseases.',
  icon: ClipboardList,
  color: 'bg-amber-600',
  requirements: ['Application to Commissioner Animal Health', 'Veterinary health certificate from exporting country', 'Import risk analysis approval', 'Quarantine arrangements (for live animals)'],
  fee: 'UGX 150,000 per permit',
  processing: '7-14 working days'
}, {
  title: 'Fish Export Certificate',
  description: 'Mandatory for all fish and fishery product exports. Ensures compliance with EU and international food safety standards for fish exports.',
  icon: FileCheck,
  color: 'bg-cyan-600',
  requirements: ['Registration as approved fish exporter', 'HACCP compliance certificate', 'Laboratory analysis results', 'Inspection at approved processing facility'],
  fee: 'UGX 100,000 per consignment',
  processing: '2-3 working days'
}, {
  title: 'Pesticide Import License',
  description: 'Required for importing agricultural chemicals and pesticides into Uganda. Ensures only registered and approved chemicals enter the country.',
  icon: AlertCircle,
  color: 'bg-red-600',
  requirements: ['Application to Agricultural Chemicals Board', 'Product registration certificate', 'Material Safety Data Sheet (MSDS)', 'Certificate of analysis from manufacturer'],
  fee: 'UGX 200,000 per license',
  processing: '14-21 working days'
}];
export function ImportExportPage() {
  return <>
      <Header data-id="element-858" />
      <main className="flex-grow pt-[160px] md:pt-[148px] bg-gray-50 pb-20" data-id="element-859">
        {/* Hero */}
        <div className="relative text-white py-32 md:py-48 overflow-hidden" data-id="element-860">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/jChwMV1UcjdM8hMBjCJe8f/fdhjkl_(1).jpg")'
        }} data-id="element-861"></div>
          <div className="absolute inset-0 bg-uganda-green/80" data-id="element-862"></div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-8" data-id="element-863">
            <div className="flex items-center text-sm text-green-100 mb-6" data-id="element-864">
              <Link to="/" className="hover:text-white flex items-center" data-id="element-865">
                <Home className="w-4 h-4 mr-1" data-id="element-866" /> Home
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-867" />
              <span data-id="element-868">Services</span>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-869" />
              <span className="text-white font-medium" data-id="element-870">Import & Export</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-871">
              Import & Export Services
            </h1>
            <p className="text-lg text-green-50 max-w-3xl leading-relaxed" data-id="element-872">
              Obtain permits, phytosanitary certificates, and veterinary health
              certificates for agricultural imports and exports. Ensuring
              compliance with national and international trade standards.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-12" data-id="element-873">
          {/* Process Overview */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-12" data-id="element-874">
            <h2 className="text-2xl font-bold text-uganda-black mb-6" data-id="element-875">
              How It Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-876">
              {[{
              step: '1',
              title: 'Submit Application',
              desc: 'Complete the relevant application form and attach required documents.'
            }, {
              step: '2',
              title: 'Pay Fees',
              desc: 'Make payment at any designated bank or through mobile money.'
            }, {
              step: '3',
              title: 'Inspection',
              desc: 'Schedule an inspection of your consignment with the relevant directorate.'
            }, {
              step: '4',
              title: 'Receive Certificate',
              desc: 'Collect your permit or certificate from the issuing office.'
            }].map((item, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-877">
                  <div className="w-12 h-12 rounded-full bg-uganda-green text-white flex items-center justify-center text-lg font-bold mb-3" data-id="element-878">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-uganda-black mb-1" data-id="element-879">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-880">{item.desc}</p>
                  {i < 3 && <ArrowRight className="w-5 h-5 text-gray-300 mt-4 hidden md:block rotate-0" data-id="element-881" />}
                </div>)}
            </div>
          </div>

          {/* Permit Types */}
          <h2 className="text-2xl font-bold text-uganda-black mb-6" data-id="element-882">
            Permits & Certificates
          </h2>
          <div className="space-y-6" data-id="element-883">
            {permitTypes.map((permit, index) => {
            const Icon = permit.icon;
            return <motion.div key={permit.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.08
            }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-884">
                  <div className="flex flex-col lg:flex-row" data-id="element-885">
                    <div className="lg:w-2/3 p-6 md:p-8" data-id="element-886">
                      <div className="flex items-center gap-4 mb-4" data-id="element-887">
                        <div className={`w-12 h-12 rounded-xl ${permit.color} flex items-center justify-center`} data-id="element-888">
                          <Icon className="w-6 h-6 text-white" data-id="element-889" />
                        </div>
                        <h3 className="text-xl font-bold text-uganda-black" data-id="element-890">
                          {permit.title}
                        </h3>
                      </div>
                      <p className="text-gray-600 leading-relaxed mb-6" data-id="element-891">
                        {permit.description}
                      </p>
                      <h4 className="text-sm font-bold text-uganda-black mb-3 uppercase tracking-wider" data-id="element-892">
                        Requirements
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-893">
                        {permit.requirements.map((req, i) => <div key={i} className="flex items-start gap-2" data-id="element-894">
                            <CheckCircle2 className="w-4 h-4 text-uganda-green flex-shrink-0 mt-0.5" data-id="element-895" />
                            <span className="text-sm text-gray-600" data-id="element-896">{req}</span>
                          </div>)}
                      </div>
                    </div>
                    <div className="lg:w-1/3 bg-gray-50 p-6 md:p-8 border-t lg:border-t-0 lg:border-l border-gray-200 flex flex-col justify-between" data-id="element-897">
                      <div className="space-y-4 mb-6" data-id="element-898">
                        <div data-id="element-899">
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1" data-id="element-900">
                            Fee
                          </p>
                          <p className="text-lg font-bold text-uganda-black" data-id="element-901">
                            {permit.fee}
                          </p>
                        </div>
                        <div data-id="element-902">
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1" data-id="element-903">
                            Processing Time
                          </p>
                          <p className="text-lg font-bold text-uganda-black" data-id="element-904">
                            {permit.processing}
                          </p>
                        </div>
                      </div>
                      <button className="w-full flex items-center justify-center gap-2 bg-uganda-green text-white py-3 rounded-lg font-semibold hover:bg-green-800 transition-colors" data-id="element-905">
                        <Download className="w-4 h-4" data-id="element-906" /> Download Form
                      </button>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </main>
      <Footer data-id="element-907" />
    </>;
}